import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, Loader2, CheckCircle, AlertCircle, ArrowLeft,
  User, Mail, Phone, MapPin, FileText, Lock, Users
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const B2BPartnerPortal = () => {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [portalStatus, setPortalStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('login');
  
  // Login state
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Register state
  const [registerForm, setRegisterForm] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    address: '',
    gst_number: '',
    message: ''
  });
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  useEffect(() => {
    checkPortalStatus();
  }, [tenantSlug]);

  const checkPortalStatus = async () => {
    try {
      const response = await api.get(`/b2b/portal-status/${tenantSlug}`);
      setPortalStatus(response.data);
    } catch (error) {
      setPortalStatus({ enabled: false, company_name: 'Unknown' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await api.post(`/auth/login`, {
        email: loginForm.email,
        password: loginForm.password,
        tenant_slug: tenantSlug
      });
      
      // API returns access_token not token
      if (response.data.access_token) {
        // Set localStorage directly and do a full page navigation
        // This ensures the auth context picks up the new state
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('refreshToken', response.data.refresh_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast.success('Login successful!');
        // Full page navigation to ensure auth context reloads
        window.location.href = `/${tenantSlug}/dashboard`;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setRegisterLoading(true);
    
    try {
      const response = await api.post(`/b2b/register/${tenantSlug}`, {
        company_name: registerForm.company_name,
        contact_person: registerForm.contact_person,
        email: registerForm.email,
        phone: registerForm.phone,
        password: registerForm.password,
        address: registerForm.address,
        gst_number: registerForm.gst_number,
        message: registerForm.message
      });
      
      setRegistrationSuccess(true);
      if (response.data.auto_approved) {
        toast.success('Registration approved! You can now login.');
        setActiveTab('login');
        setLoginForm({ email: registerForm.email, password: registerForm.password });
      } else {
        toast.success('Registration submitted! Awaiting approval.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setRegisterLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (!portalStatus?.enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">B2B Portal Not Available</h2>
            <p className="text-gray-600 mb-6">
              The B2B partner portal is not currently enabled for {portalStatus?.company_name || 'this agency'}.
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4" data-testid="b2b-portal">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left side - Info */}
        <div className="text-white space-y-6 p-6 lg:p-10">
          <Button 
            variant="ghost" 
            className="text-white/70 hover:text-white hover:bg-white/10 -ml-2"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          
          <div className="flex items-center gap-4">
            {portalStatus?.logo_url ? (
              <img src={portalStatus.logo_url} alt={portalStatus.company_name} className="h-16 w-auto" />
            ) : (
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center">
                <Building2 className="w-8 h-8 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold">{portalStatus?.company_name}</h1>
              <p className="text-white/70">B2B Partner Portal</p>
            </div>
          </div>
          
          <div className="space-y-4 mt-8">
            <h2 className="text-xl font-semibold">Partner Benefits</h2>
            <ul className="space-y-3">
              {[
                'Access exclusive B2B rates and packages',
                'Create quotations with your branding',
                'Manage your own sub-agents',
                'Real-time availability and pricing',
                'Dedicated partner support'
              ].map((benefit, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-white/80">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {portalStatus?.registration_message && (
            <div className="mt-6 p-4 bg-white/10 rounded-xl border border-white/20">
              <p className="text-white/90 text-sm">{portalStatus.registration_message}</p>
            </div>
          )}
        </div>
        
        {/* Right side - Forms */}
        <Card className="shadow-2xl bg-white border-slate-200">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl text-[#0a192f]">Partner Access</CardTitle>
            <CardDescription className="text-[#0a192f]/70">Login or register as a B2B partner</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-100">
                <TabsTrigger value="login" className="data-[state=active]:bg-white data-[state=active]:text-[#0a192f]">Login</TabsTrigger>
                <TabsTrigger value="register" className="data-[state=active]:bg-white data-[state=active]:text-[#0a192f]">Register</TabsTrigger>
              </TabsList>
              
              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-[#0a192f]">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="partner@example.com"
                        className="pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-[#0a192f]">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="Enter your password"
                        className="pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-cyan-500/25" disabled={loginLoading}>
                    {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Sign In
                  </Button>
                  
                  {/* Demo Credential */}
                  <div className="pt-3 border-t border-[#0a192f]/10">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-cyan-500/10 to-teal-500/10 hover:from-cyan-500/20 hover:to-teal-500/20 transition-colors border border-cyan-500/20"
                      onClick={() => {
                        setLoginForm({ email: 'partner@demo.com', password: 'Demo@2026' });
                        toast.success('Demo credentials filled');
                      }}
                      data-testid="demo-b2b-login-btn"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-lg flex items-center justify-center">
                          <Users className="w-4 h-4 text-white" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-medium text-[#0a192f]">Demo B2B Partner</p>
                          <p className="text-xs text-cyan-600">Click to fill demo credentials</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </form>
              </TabsContent>
              
              {/* Register Tab */}
              <TabsContent value="register">
                {registrationSuccess && !portalStatus?.auto_approve ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-[#0a192f] mb-2">Registration Submitted!</h3>
                    <p className="text-[#0a192f]/70 mb-6">
                      Your registration is pending approval. You'll be notified once your account is activated.
                    </p>
                    <Button variant="outline" onClick={() => setActiveTab('login')} className="border-[#0a192f]/30 text-[#0a192f] hover:bg-[#0a192f]/5">
                      Go to Login
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company_name" className="text-[#0a192f]">Company Name *</Label>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                          <Input
                            id="company_name"
                            placeholder="Your Travel Agency"
                            className="pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                            value={registerForm.company_name}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, company_name: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact_person" className="text-[#0a192f]">Contact Person *</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                          <Input
                            id="contact_person"
                            placeholder="Your Name"
                            className="pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                            value={registerForm.contact_person}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, contact_person: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="reg-email" className="text-[#0a192f]">Email *</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                          <Input
                            id="reg-email"
                            type="email"
                            placeholder="email@company.com"
                            className="pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                            value={registerForm.email}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, email: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-[#0a192f]">Phone / WhatsApp</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                          <Input
                            id="phone"
                            placeholder="+91 98765 43210"
                            className="pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                            value={registerForm.phone}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, phone: e.target.value }))}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="reg-password" className="text-[#0a192f]">Password *</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                          <Input
                            id="reg-password"
                            type="password"
                            placeholder="Min 6 characters"
                            className="pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                            value={registerForm.password}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, password: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password" className="text-[#0a192f]">Confirm Password *</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                          <Input
                            id="confirm-password"
                            type="password"
                            placeholder="Confirm password"
                            className="pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                            value={registerForm.confirmPassword}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="gst_number" className="text-[#0a192f]">GST Number</Label>
                      <div className="relative">
                        <FileText className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                        <Input
                          id="gst_number"
                          placeholder="22AAAAA0000A1Z5"
                          className="pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                          value={registerForm.gst_number}
                          onChange={(e) => setRegisterForm(prev => ({ ...prev, gst_number: e.target.value }))}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="address" className="text-[#0a192f]">Business Address</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                        <Input
                          id="address"
                          placeholder="City, State"
                          className="pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                          value={registerForm.address}
                          onChange={(e) => setRegisterForm(prev => ({ ...prev, address: e.target.value }))}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-[#0a192f]">Message (Optional)</Label>
                      <textarea
                        id="message"
                        placeholder="Tell us about your business..."
                        className="w-full px-3 py-2 border border-[#0a192f]/30 rounded-md text-sm text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20 bg-white"
                        rows={2}
                        value={registerForm.message}
                        onChange={(e) => setRegisterForm(prev => ({ ...prev, message: e.target.value }))}
                      />
                    </div>
                    
                    <Button type="submit" className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-cyan-500/25" disabled={registerLoading}>
                      {registerLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
                      Submit Registration
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default B2BPartnerPortal;
